<script setup lang="ts">
import { computed, ref } from "vue";
import { usePageLanguage } from "@/composables/usePageLanguage";
import { useConfigStore } from "@/stores/store.config";
import { getLanguageName } from "@/utils/general";

const emit = defineEmits<{ close: [] }>();
const { changeLanguage, languageSwitchState } = usePageLanguage();
const user = useConfigStore().useUserStore();
const languages = computed<string[]>(
  () => user.homepageConfig?.widgetLanguages || [],
);
const languageItems = computed(() => {
  const names = user.homepageConfig?.languageName || {};
  const defaultLang = user.defaultLang || "en";

  return languages.value
    .map((value) => ({
      value,
      label: names[value] || value.toUpperCase(),
    }))
    .sort(
      (a, b) =>
        Number(b.value === defaultLang) - Number(a.value === defaultLang),
    );
});
const selected = ref(
  languages.value.includes(user.defaultLang)
    ? user.defaultLang
    : languages.value[0] || "en",
);
const buttonStyle = computed(() => ({
  backgroundColor:
    user.homepageConfig?.homepage?.buttonRedirectionColor || "#3377ff",
  color: user.homepageConfig?.homepage?.buttonTextRedirectionColor || "#ffffff",
}));

const proceed = async () => {
  if (!selected.value) return;

  if (!(await changeLanguage(selected.value))) return;

  const dataLayer = (
    window as Window & { dataLayer?: Array<Record<string, unknown>> }
  ).dataLayer;
  dataLayer?.push({
    event: "select_first_language",
    vouch_product: "homepage",
    merchant_id: user.merchantId || "",
    language_code: selected.value,
    language_name: getLanguageName(selected.value || "en"),
  });

  user.updateSelectedPreferredLang();
  emit("close");
};
</script>

<template>
  <div class="overlay">
    <section
      class="card"
      role="dialog"
      aria-modal="true"
      aria-label="Preferred language"
    >
      <h2 class="title">Preferred Language</h2>
      <div class="language-list">
        <label
          v-for="lang in languageItems"
          :key="lang.value"
          class="language-option"
          :class="{ selected: selected === lang.value }"
        >
          <input
            v-model="selected"
            type="radio"
            name="preferred-language"
            :value="lang.value"
          />
          <span>{{ lang.label }}</span>
        </label>
      </div>
      <button
        class="proceed"
        type="button"
        :style="buttonStyle"
        :disabled="
          !selected ||
          languageSwitchState.pending ||
          languageSwitchState.initializing
        "
        @click="proceed"
      >
        Proceed
      </button>
    </section>
  </div>
</template>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  z-index: 40;
  display: grid;
  place-items: center;
  background: rgba(255, 255, 255, 0.8);
}
.card {
  width: min(340px, calc(100% - 16px));
  padding: 25px 20px;
  border-radius: 8px;
  background: white;
  box-shadow:
    -4px 4px 4px 2px #f0f0f0,
    4px 4px 4px 2px #f0f0f0;
}
.title {
  margin: 0;
  font-size: var(--font-size-title);
  font-weight: var(--font-weight-medium);
  color: #333;
}
.language-list {
  display: flex;
  box-sizing: border-box;
  max-height: 300px;
  flex-direction: column;
  gap: 10px;
  margin: 20px 0 10px;
  overflow-x: hidden;
  overflow-y: auto;
}
.language-option {
  display: flex;
  box-sizing: border-box;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 12px;
  border: 1px solid #cecece;
  border-radius: 4px;
  color: #000;
  cursor: pointer;
  font-size: var(--font-size-body);
}
.language-option.selected {
  border-color: #3377ff;
  background: rgba(51, 119, 255, 0.1);
  color: #3377ff;
}
.language-option input {
  accent-color: #3377ff;
}
.proceed {
  width: 100%;
  min-height: 40px;
  margin: 8px 0;
  border: 0;
  border-radius: 8px;
  cursor: pointer;
  font-family: var(--font-sans);
  font-weight: var(--font-weight-bold);
}
.proceed:disabled {
  background: #eaeaea !important;
  color: #777 !important;
  cursor: default;
}
</style>
